import 'react-toastify/dist/ReactToastify.css';
import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAsyncEffect } from 'rooks';
import { ItemAclModal } from '@/accessControl/ItemAclModal.molecule';
import { useAgentBuilderQuery } from '@/agentBuilder/agentBuilder.queries';
import { useCurrentAgentBuilderId } from '@/agentBuilder/agentBuilder.store';
import { HeaderAIAssistant } from '@/aiAssistant/HeaderAIAssistant.atom';
import { shouldOfferCapturesManagement } from '@/captures/captures.utilities';
import { EditableText } from '@/core/EditableText.atom';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { sqItemTableDefinitionStore, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { setShowModal } from '@/core/hooks/useModalManager.hook';
import { AccessKeyModal } from '@/header/AccessKeyModal.organism';
import { EditUserPreferencesModal } from '@/header/EditUserPreferencesModal.atom';
import { HamburgerMenu } from '@/header/HamburgerMenu.atom';
import { HeaderBreadcrumbs } from '@/header/HeaderBreadcrumbs.atom';
import { HeaderLogo } from '@/header/HeaderLogo.atom';
import { ImpactReportMenu } from '@/header/ImpactReportMenu.atom';
import { LayoutControls } from '@/header/LayoutControls.atom';
import { LockedTopicActions } from '@/header/LockedTopicActions.molecule';
import { ViewModeWorksheetActions } from '@/header/ViewModeWorksheetActions.molecule';
import { WorkbookHeaderControls } from '@/header/WorkbookHeaderControls.molecule';
import { WorkstepIcons } from '@/header/WorkstepIcons.atom';
import { headerStyles as styles } from '@/header/header.styles';
import { setBranchCode } from '@/homescreen/homescreen.actions';
import { knowledgeManagementEnabled } from '@/knowledgeManagement/knowledgeManagement.availability';
import { useLicenseConfiguration, useLicenseManagementActions } from '@/licenseManagement/licenseManagement.store';
import { LoadingSpinner } from '@/main/LoadingSpinner.molecule';
import { AUTO_WIDTH_OFFSET, INPUT_MIN_SIZE } from '@/main/app.constants';
import { isDataLabAiAssistant, isEmbeddedView } from '@/main/routing.utilities';
import { useRoutingData } from '@/main/useRoutingData.hook';
import { canManageItem, canModifyWorkbook, canWriteItem } from '@/services/authorization.service';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import {
  isAccessKeyCreationEnabled,
  isImpactReportsToolEnabled,
  isONNXToolEnabled,
  isVantageEnabled,
  newHeaderExperienceEnabled,
} from '@/services/systemConfiguration.utilities';
import { useSkillQuery } from '@/skillsBuilder/queries';
import { asPermissionedItem } from '@/skillsBuilder/utilities';
import { TableDefinitionSettingsModal } from '@/tableDefinitionEditor/TableDefinitionSettingsModal.molecule';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { TreeBuilderHeader } from '@/treeBuilder/TreeBuilderHeader';
import { logout as logoutSqAuth } from '@/utilities/authentication.utilities';
import { isMobileDevice } from '@/utilities/utilities';
import {
  isEditWorkbookMode,
  isPresentationWorkbookMode,
  isViewOnlyWorkbookMode,
} from '@/utilities/workbookMode.utilities';
import { setUserPreferencesDisplay, setUserPreferencesTab } from '@/workbench/workbench.actions';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';
import { renameWorksheet, setWorkbookName } from '@/workbook/workbook.actions';
import { WorksheetWatchers } from '@/worksheets/WorksheetWatchers.atom';
import {
  faCog,
  faEnvelope,
  faGaugeHigh,
  faImage,
  faKey,
  faLock,
  faRefresh,
  faRightFromBracket,
  faUser,
} from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import {
  fcBellCog,
  fcExternalMl,
  fcFolder,
  fcSeeqDatalab,
  fcUserGroupCirclePlus,
  fcVantageNotification,
} from '@seeqdev/icons/seeq';
import { Icon, TriggerWithDropdown } from '@seeqdev/qomponents';
import type { IconDefinition } from '@seeqdev/icons';

const BAR_ACCENT_BY_THEME = {
  brand: '#2a5c84',
  analysis: '#00a059',
  topic: '#00acdb',
  vantage: '#a35fec',
  insights: '#0076D1',
};

interface GenerateDropdownEntryOptions {
  iconClass?: IconDefinition;
  iconExtraClassNames?: string;
  translationKey?: string;
  action?: () => void;
  display?: boolean;
  divider?: boolean;
  testId?: string;
}

export const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location ? location.pathname : '';
  const [searchParams] = useSearchParams();
  const { isWorksheetDisplayed, isTreeBuilder, workbookDisplayMode } = useRoutingData();
  const [modalShown, setModalShown] = useState<'accessControl' | 'accessKeys' | 'none'>('none');
  const ancestors = useFluxPath(sqWorkbookStore, (store) => store.ancestors);
  const userHasManagePermission = useFluxPath(sqWorkbookStore, (store) => store.effectivePermissions.manage);
  const stateParams = useFluxPath(sqWorkbenchStore, (store) => store.stateParams);
  const isEditMode = useFluxPath(sqWorkbookStore, (store) => isEditWorkbookMode(store.workbookDisplay));
  const isViewMode = useFluxPath(sqWorkbookStore, (store) => isViewOnlyWorkbookMode(store.workbookDisplay));
  const isPresentationMode = isWorksheetDisplayed && isPresentationWorkbookMode(workbookDisplayMode);
  const isEmbedded = isEmbeddedView();

  const {
    workbookId,
    name: workbookName,
    owner,
    effectivePermissions,
    isTopic,
    isLocked,
    lockedChangeMade,
    isVantage,
    isAnalysis,
    isActionBoard,
  } = useFluxPaths(sqWorkbookStore, [
    'workbookId',
    'name',
    'owner',
    'effectivePermissions',
    'isTopic',
    'isLocked',
    'lockedChangeMade',
    'isVantage',
    'isAnalysis',
    'isActionBoard',
  ]);
  const { displayUserPreferences: storeDisplayUserPreferences, currentUser } = useFluxPaths(sqWorkbenchStore, [
    'displayUserPreferences',
    'currentUser',
  ]);
  const worksheetName = useFluxPath(sqWorkbookStore, (store) => store.getWorksheetName(stateParams.worksheetId));
  const {
    name: tableName,
    id: tableId,
    tableDefinitionOutput,
  } = useFluxPaths(sqItemTableDefinitionStore, ['name', 'id', 'tableDefinitionOutput']);

  const currentAgentBuilderId = useCurrentAgentBuilderId();
  const currentAgentBuilder = useAgentBuilderQuery(currentAgentBuilderId).data ?? undefined;
  const isAgentBuilderCanvas =
    pathname.startsWith('/agent-builder/') &&
    !pathname.startsWith('/agent-builder/skills/') &&
    searchParams.get('template') !== 'true' &&
    !!currentAgentBuilder?.id;
  const agentBuilderHasManagePermission = canManageItem(currentAgentBuilder);

  // The Skills editor mirrors the agent-builder canvas: surface the same header
  // Share control, keyed on the skill's backing DLF project (skills aren't Items
  // themselves). The "new" route has no project yet, so no Share.
  const skillsCanvasMatch = pathname.match(/^\/agent-builder\/skills\/([^/?]+)/);
  const currentSkillId = skillsCanvasMatch && skillsCanvasMatch[1] !== 'new' ? skillsCanvasMatch[1] : undefined;
  const currentSkill = useSkillQuery(currentSkillId).data ?? undefined;
  const isSkillsCanvas = !!currentSkill?.metadata?.project_id;
  const skillHasManagePermission = canManageItem(asPermissionedItem(currentSkill));
  const licenseConfiguration = useLicenseConfiguration();
  const { setShowOfficeHours } = useLicenseManagementActions();

  const [toolDisplayName, setToolDisplayName] = useState('');

  useEffect(() => {
    if (location?.pathname?.startsWith('/administration')) {
      setToolDisplayName('Administration');
    } else if (location?.pathname?.startsWith('/agent-builder')) {
      setToolDisplayName(t('AGENT_BUILDER.TITLE'));
    } else if (isTreeBuilder) {
      setToolDisplayName(t('TREE_BUILDER.TITLE'));
    } else if (!(location?.pathname?.includes('/worksheet') || location?.pathname?.includes('/view'))) {
      setToolDisplayName('');
    } else if (isVantage) {
      setToolDisplayName('Vantage');
    } else if (isActionBoard) {
      setToolDisplayName('Insight');
    } else if (isAnalysis) {
      setToolDisplayName('Workbench');
    } else if (isTopic) {
      setToolDisplayName('Organizer');
    } else {
      setToolDisplayName('');
    }
  }, [isVantage, isActionBoard, isAnalysis, isTopic, isTreeBuilder, location, t]);

  useAsyncEffect(async () => {
    const setEmptyState = () => {
      setBranchCode('');
      setShowOfficeHours(false);
    };

    try {
      const serviceUrl = licenseConfiguration?.['Seeq Usage']?.['Service URL'] ?? '';
      const accessKey = licenseConfiguration?.['Seeq Usage']?.['Access Key'] ?? '';

      if (!serviceUrl || !accessKey) {
        setEmptyState();
        return;
      }

      const customerPath = serviceUrl.match(/usage\.seeq\.com\/(.*)/)?.[1] ?? '';
      const baseUrl = `https://telemetry.seeq.com/usage/${customerPath}`;

      const response = await fetch(`${baseUrl}/successPlan`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessKey}`,
        },
      });

      if (response?.status !== 200) {
        setEmptyState();
        return;
      }

      const resJson = await response.json();
      const { 'Office Hours Included': officeHoursIncluded, 'Training Branch Code': trainingBranchCode } = resJson;
      setBranchCode(trainingBranchCode || t('HOME_SCREEN.TRAINING.NO_CODE_ERROR_MESSAGE'));
      setShowOfficeHours(!!officeHoursIncluded);
    } catch (e) {
      console.error('Error fetching telemetry data:', e);
      setEmptyState();
    }
  }, []);

  const currentUserMissingInfo = useFluxPath(sqWorkbenchStore, (store) => store.isCurrentUserMissingInfo());
  const userIsTrusted = useFluxPath(sqWorkbenchStore, (store) => store.currentUser.isTrusted);

  const displayUserPreferences = storeDisplayUserPreferences && !isPresentationMode && !headlessRenderMode();

  const workbook = {
    name: workbookName,
    owner,
    effectivePermissions,
  };
  const breadcrumbs = isEditMode && canModifyWorkbook(workbook) ? ancestors : [];
  const headerNameStyle = [styles.name, breadcrumbs.length > 0 && styles.nameWithBreadcrumbs];

  const getColor = () => {
    if (!(pathname && (pathname.includes('/worksheet') || pathname.includes('/view')))) {
      // we need to check the current location so we can ensure the bar shows the correct color when a new workbook.
      // is created
      return BAR_ACCENT_BY_THEME.brand;
    } else if (isVantage) {
      return BAR_ACCENT_BY_THEME.vantage;
    } else if (isActionBoard) {
      return BAR_ACCENT_BY_THEME.insights;
    } else if (isAnalysis) {
      return BAR_ACCENT_BY_THEME.analysis;
    } else if (isTopic) {
      return BAR_ACCENT_BY_THEME.topic;
    } else {
      return BAR_ACCENT_BY_THEME.brand;
    }
  };

  useEffect(() => {
    setBarColor(getColor());
  }, [isVantage, isActionBoard, isAnalysis, isTopic, location]);

  const [barColor, setBarColor] = useState(getColor());

  const generateDropdownEntry = (
    {
      iconClass,
      iconExtraClassNames = '',
      translationKey = '',
      action = _.noop,
      divider = false,
      testId = '',
    }: GenerateDropdownEntryOptions,
    index: number,
  ) => {
    return {
      key: index,
      icon: iconClass,
      iconExtraClassNames,
      itemXstyle: styles.menuItem,
      labelXstyle: styles.menuItem,
      labelTestId: `moreActions_${translationKey}`,
      iconTestId: `moreActionsIcon_${translationKey}`,
      onClick: action,
      label: t(translationKey),
      hasDivider: divider,
      testId,
    };
  };

  const actions = [
    {
      href: '#',
      iconClass: faCog,
      translationKey: 'USER.EDIT_PREFERENCES',
      action: () => {
        doTrack(TRACK.MENU, 'Edit Preferences', 'open');
        if (currentUserMissingInfo) {
          setUserPreferencesTab(PREFERENCE_TABS.PROFILE);
        } else {
          setUserPreferencesTab(PREFERENCE_TABS.SYSTEM);
        }
        setUserPreferencesDisplay(true);
      },
      display: true,
      testId: 'editPreferences',
    },
    {
      href: '#',
      iconClass: faEnvelope,
      translationKey: 'NOTIFICATIONS_HISTORY.TITLE',
      action: () => {
        doTrack(TRACK.MENU, 'Notifications History', 'open');
        window.open('/notifications-history', 'SeeqNotificationsHistory');
      },
      display: userIsTrusted,
      testId: 'notifications-history',
      divider: true,
    },
    {
      href: '#',
      iconClass: faImage,
      translationKey: 'CAPTURES.MANAGEMENT.TITLE',
      action: () => {
        doTrack('Menu', 'Captures Management', 'open');
        window.open('/captures', 'SeeqCaptures');
      },
      display: shouldOfferCapturesManagement(userIsTrusted),
      testId: 'captures',
    },
    {
      href: '#',
      iconClass: fcBellCog,
      translationKey: 'NOTIFICATIONS_MANAGEMENT.TITLE',
      action: () => {
        doTrack(TRACK.MENU, 'Notifications Management', 'open');
        window.open('/notifications-management', 'SeeqNotificationsManagement');
      },
      display: userIsTrusted,
      testId: 'notifications',
    },
    {
      href: '#',
      iconClass: fcVantageNotification,
      translationKey: 'VANTAGE.MANAGEMENT.TITLE',
      action: () => {
        doTrack(TRACK.MENU, 'Vantage Management', 'open');
        window.open('/vantage-management', 'SeeqVantageManagement');
      },
      display: userIsTrusted && isVantageEnabled(),
      testId: 'vantages',
    },
    {
      href: '#',
      iconClass: fcExternalMl,
      iconExtraClassNames: stylex.props(styles.externalMlIcon).className,
      translationKey: 'EXTERNAL_ML_MANAGEMENT.TITLE',
      action: () => {
        doTrack(TRACK.MENU, 'External ML Management', 'open');
        window.open('/external-ml-management', 'ExternalMLManagement');
      },
      display: userIsTrusted && isONNXToolEnabled(),
      testId: 'externalMLManagement',
    },
    {
      href: '#',
      iconClass: fcSeeqDatalab,
      translationKey: 'DATA_LAB_MANAGEMENT.TITLE',
      action: () => {
        doTrack(TRACK.MENU, 'Data Lab Management', 'open');
        window.open('/manage-data-lab', 'DataLabManagement');
      },
      display: userIsTrusted,
      testId: 'dataLabManagement',
    },
    {
      href: '#',
      iconClass: fcFolder,
      translationKey: 'KNOWLEDGE_MANAGEMENT.NAV.TITLE',
      action: () => {
        doTrack(TRACK.MENU, 'Knowledge Management', 'open');
        window.open('/knowledge-management', 'SeeqKnowledgeManagement');
      },
      display: userIsTrusted && knowledgeManagementEnabled(),
      testId: 'knowledgeManagement',
      divider: true,
    },
    {
      href: '#',
      iconClass: faGaugeHigh,
      translationKey: 'USAGE.MENU_LABEL',
      action: () => {
        doTrack(TRACK.MENU, 'Usage', 'open');
        window.open('/my-usage', 'UserUsage');
      },
      display: userIsTrusted,
      testId: 'user-usage',
      divider: true,
    },
    {
      href: '#',
      iconClass: faKey,
      translationKey: 'ACCESS_KEY.ACCESS_KEYS',
      action: () => {
        doTrack(TRACK.MENU, 'Access Keys', 'open');
        setModalShown('accessKeys');
      },
      display: userIsTrusted,
      testId: 'accessKeys',
      divider: true,
    },
    {
      href: '#',
      iconClass: faRightFromBracket,
      translationKey: 'LOGOUT',
      action: logout,
      display: true,
      testId: 'logout',
    },
  ];

  function logout() {
    doTrack(TRACK.MENU, 'Logout', 'click');
    logoutSqAuth(_.join(_.compact([pathname === '/' ? '' : pathname, searchParams.toString()]), '?'));
  }

  /** The "Share button" in the toolbar is only shown when in a worksheet that is either locked or editable by this
   *  user (note that both Organizer Topics and Analyses contain "worksheets").
   */
  const showShareButton = () => {
    return (
      !isEmbedded &&
      ((isWorksheetDisplayed && ((canModifyWorkbook(workbook) && isEditMode) || isLocked)) ||
        (isTreeBuilder && tableDefinitionOutput?.id && canWriteItem(tableDefinitionOutput)) ||
        isAgentBuilderCanvas ||
        isSkillsCanvas)
    );
  };

  return (
    <div {...stylex.props(styles.wrapper)}>
      <header
        {...stylex.props(styles.header, styles.printHidden, newHeaderExperienceEnabled() && styles.brandedHeader)}
        data-new-header={newHeaderExperienceEnabled()}
        data-testid="header"
        id="header">
        <div {...stylex.props(styles.brand)}>
          <HeaderLogo disableLink={isEmbedded} />
          {/* The text spans share a baseline with each other; the spinner is centered against them,
              so that whether it renders (it is suppressed in Vantage) cannot shift them. */}
          {newHeaderExperienceEnabled() && (
            <div data-testid="headerProductName" {...stylex.props(styles.productName)}>
              <span {...stylex.props(styles.productBaseline)}>
                <span {...stylex.props(styles.productInitial)}>{toolDisplayName.substring(0, 1)}</span>
                <span data-testid="headerProductRemainder" {...stylex.props(styles.productRemainder)}>
                  {toolDisplayName.substring(1)}
                </span>
              </span>
              {!isVantage && <LoadingSpinner />}
            </div>
          )}
        </div>
        {isTreeBuilder ? (
          <>
            {/* Empty spacer keeps the logo and the trailing action buttons pushed to their normal flex
                positions now that the title below is taken out of flow and centered on the full header
                width (so it stays aligned with TreeBuilderProgressTracker regardless of which action
                buttons happen to render). */}
            <div {...stylex.props(styles.titleSpacer)} />
            <div {...stylex.props(styles.treeTitle)}>
              <TreeBuilderHeader
                tableId={tableId}
                name={tableName}
                tableScopedTo={tableDefinitionOutput?.scopedTo}
                workbookName={workbookName}
                workbookId={workbookId}
                canWrite={tableDefinitionOutput?.id ? canWriteItem(tableDefinitionOutput) : true}
              />
            </div>
          </>
        ) : (
          <div {...stylex.props(styles.titleColumn)}>
            <div {...stylex.props(styles.titleRow)}>
              <div {...stylex.props(styles.titleContent)}>
                <HeaderBreadcrumbs breadcrumbs={breadcrumbs} isWorksheetDisplayed={isWorksheetDisplayed} />
                <div id="workbookFullName" {...stylex.props(styles.fullName)}>
                  {stateParams.worksheetId && (
                    <EditableText
                      allowEditing={canModifyWorkbook(workbook) && isEditMode}
                      onUpdate={(name) => setWorkbookName(stateParams.workbookId, name, workbookName)}
                      value={workbookName}
                      inputXstyle={styles.nameInput}
                      textXstyle={headerNameStyle}
                      testId="workbookName"
                      id="workbookName"
                      minWidth={INPUT_MIN_SIZE}
                      offsetWidth={AUTO_WIDTH_OFFSET}
                    />
                  )}
                  {workbookName && stateParams.worksheetId && <p {...stylex.props(styles.nameSeparator)}>-</p>}
                  {stateParams.worksheetId && (
                    <EditableText
                      allowEditing={canModifyWorkbook(workbook) && isEditMode}
                      onUpdate={(name) => renameWorksheet(stateParams.worksheetId, name)}
                      value={stateParams.worksheetId && worksheetName!}
                      inputXstyle={styles.nameInput}
                      textXstyle={headerNameStyle}
                      testId="worksheetName"
                      id="worksheetName"
                      minWidth={INPUT_MIN_SIZE}
                      offsetWidth={AUTO_WIDTH_OFFSET}
                    />
                  )}
                </div>
              </div>
              {stateParams.worksheetId ? <WorkbookHeaderControls /> : null}
            </div>
          </div>
        )}

        {stateParams.worksheetId && <WorkstepIcons isEditMode={isEditMode} isViewMode={isViewMode} />}

        {isWorksheetDisplayed && lockedChangeMade ? (
          <HoverTooltip text="ACCESS_CONTROL.LOCKING.CHANGED_TOOLTIP" placement="bottom">
            <div>
              <Icon
                icon={faRefresh}
                testId="lockRefreshIcon"
                type="white"
                xstyle={styles.refreshIcon}
                onClick={() => window.location.reload()}
              />
            </div>
          </HoverTooltip>
        ) : null}
        {isLocked && stateParams.worksheetId && (
          <div data-testid="toolbar-locked-message" {...stylex.props(styles.lockStatus)}>
            <HoverTooltip text="ACCESS_CONTROL.LOCKING.LOCKED_TOOLTIP" placement="bottom">
              <div {...stylex.props(styles.lockLabel)}>
                <span {...stylex.props(styles.badgePadding)}>{t('ACCESS_CONTROL.LOCKING.LOCKED')}</span>
                <Icon icon={faLock} testId="lockIcon" type="white" xstyle={styles.lockIcon} />
              </div>
            </HoverTooltip>
          </div>
        )}
        <div {...stylex.props(styles.worksheetActions)}>
          {!isLocked && stateParams.worksheetId && (
            <ViewModeWorksheetActions isEditMode={isEditMode} isViewMode={isViewMode} isLocked={isLocked} />
          )}
          {isTopic && isLocked && stateParams.worksheetId && <LockedTopicActions />}
        </div>
        {isEditMode && !isViewMode && (
          <span data-testid="worksheetWatchers">
            <WorksheetWatchers compactMode={false} worksheetId={stateParams.worksheetId} />
          </span>
        )}
        {!isPresentationMode && !isEmbedded && !isTreeBuilder && isImpactReportsToolEnabled() && userIsTrusted && (
          <div {...stylex.props(styles.impactReportAction, styles.navbarAction)} id="valueCaptureNav">
            <ImpactReportMenu readOnlyMode={isViewMode} />
          </div>
        )}
        {isTreeBuilder && showShareButton() && (
          <HoverTooltip placement="bottom" text="SCALING.TABLE.SETTINGS_TOOLTIP">
            <div
              data-testid="treeSettingsButton"
              {...stylex.props(styles.settingsAction, styles.navbarAction)}
              onClick={() => setShowModal(TableDefinitionSettingsModal, true)}>
              <div {...stylex.props(styles.actionTrigger)}>
                <Icon icon={faCog} xstyle={[styles.navigationIcon, styles.actionIcon]} type="inherit" />
              </div>
            </div>
          </HoverTooltip>
        )}
        {showShareButton() && (
          <HoverTooltip placement="bottom" text="ACL_STATUS.TOOLTIP.MANAGE_ACCESS_CONTROL">
            <div
              data-testid="manageAccessControl"
              {...stylex.props(styles.shareAction, styles.navbarAction)}
              onClick={() => {
                doTrack(TRACK.MENU, 'Access Control', 'open');
                if (isTreeBuilder) {
                  setShowModal(ItemAclModal, true);
                } else {
                  setModalShown('accessControl');
                }
              }}>
              <div {...stylex.props(styles.shareTrigger)}>
                <Icon
                  icon={fcUserGroupCirclePlus}
                  xstyle={[styles.navigationIcon, styles.shareIcon, styles.navbarAction]}
                  type="inherit"
                />
                {!isMobileDevice() && <span {...stylex.props(styles.shareLabel)}>{t('ACCESS_CONTROL.SHARE')}</span>}
              </div>
            </div>
          </HoverTooltip>
        )}
        {modalShown === 'accessControl' && isAgentBuilderCanvas && currentAgentBuilder?.id && (
          <ItemAclModal
            itemId={currentAgentBuilder.id}
            includeLinksAndCorporateMessage={false}
            closeModal={() => setModalShown('none')}
            isAclModalLocationInHeader={true}
            userHasManagePermission={agentBuilderHasManagePermission}
          />
        )}
        {modalShown === 'accessControl' && isSkillsCanvas && currentSkill?.metadata?.project_id && (
          <ItemAclModal
            itemId={currentSkill.metadata.project_id}
            itemName={currentSkill.metadata.name}
            includeLinksAndCorporateMessage={false}
            closeModal={() => setModalShown('none')}
            isAclModalLocationInHeader={true}
            userHasManagePermission={skillHasManagePermission}
          />
        )}
        {modalShown === 'accessControl' && !isAgentBuilderCanvas && !isSkillsCanvas && (
          <ItemAclModal
            itemId={stateParams.workbookId}
            workbookId={stateParams.workbookId}
            worksheetId={stateParams.worksheetId}
            folderId={stateParams.currentFolderId}
            includeLinksAndCorporateMessage={true}
            closeModal={() => setModalShown('none')}
            isAclModalLocationInHeader={true}
            userHasManagePermission={userHasManagePermission}
          />
        )}
        {!isPresentationMode && !isEmbedded && isWorksheetDisplayed && <LayoutControls />}
        {!isPresentationMode && !isEmbedded && !(isMobileDevice() && isViewMode) && currentUser.isTrusted && (
          <HeaderAIAssistant />
        )}
        {!isPresentationMode && !isEmbedded && (
          <div {...stylex.props(styles.userMenu, styles.navbarAction)} id="userMenu">
            <TriggerWithDropdown
              triggerIcon={
                <div {...stylex.props(styles.centeredActions)}>
                  <Icon
                    icon={faUser}
                    xstyle={[styles.navigationIcon, styles.userIcon]}
                    type="inherit"
                    testId="userProfileBtn"
                    id="userProfile"
                  />
                  {!isMobileDevice() && <span>{currentUser.firstName}</span>}
                </div>
              }
              id="userProfile"
              dropdownItems={actions
                .filter((action) => action.display)
                .map((action, index) => generateDropdownEntry(action, index))}
            />
          </div>
        )}
        {!isEmbedded && (
          <div {...stylex.props(styles.hamburger)}>
            <HamburgerMenu />
          </div>
        )}

        {modalShown === 'accessKeys' && (
          <AccessKeyModal onClose={() => setModalShown('none')} creationEnabled={isAccessKeyCreationEnabled()} />
        )}

        {!isEmbedded && displayUserPreferences && (
          <EditUserPreferencesModal
            closeFn={() => {
              setUserPreferencesDisplay(false);
            }}
          />
        )}
      </header>
      {newHeaderExperienceEnabled() &&
      !headlessRenderMode() &&
      !isPresentationWorkbookMode() &&
      !isDataLabAiAssistant() ? (
        <div {...stylex.props(styles.accentBar(barColor))}></div>
      ) : null}
    </div>
  );
};
